/* eslint-disable no-console */
import { getEnv } from "@/lib/utils/env";
import { isAbortError } from "@/utils/is-abort-error";
import { ApolloError } from "@apollo/client";
import pino from "pino";

const isClient = typeof window !== "undefined";
const isProduction = process.env.NODE_ENV === "production";
const appId = "next";

// Safe context gathering function - only adds unique context not already provided by Logtail
function addContextToLogger(): Record<string, unknown> | null {
	try {
		const context: Record<string, unknown> = {
			environment: process.env.NODE_ENV,
		};

		if (isClient) {
			// Session Info
			try {
				const sessionId =
					sessionStorage.getItem("session_id") || `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
				if (!sessionStorage.getItem("session_id")) {
					sessionStorage.setItem("session_id", sessionId);
				}
				context.sessionId = sessionId;
			} catch (_e) {
				// Ignore sessionStorage errors
			}

			// User Info (safely try to get from localStorage or global state)
			try {
				const userData = localStorage.getItem("user_data");
				if (userData) {
					const user = JSON.parse(userData);
					context.userId = user.id;
					context.userEmail = user.email;
					const firstGroup = Array.isArray(user.groups) ? user.groups[0] : undefined;
					context.userRelationship = user.relation || firstGroup?.relationship;
					context.isAuthenticated = true;
				} else {
					context.isAuthenticated = false;
				}
			} catch (_e) {
				context.isAuthenticated = false;
			}

			// Route Info (only add query params as path is already in URL)
			try {
				const url = new URL(window.location.href);
				const queryParams = Object.fromEntries(url.searchParams.entries());
				if (Object.keys(queryParams).length > 0) {
					context.queryParams = queryParams;
				}

				// Add referrer if available
				if (document.referrer) {
					context.referrer = document.referrer;
				}
			} catch (_e) {
				// Ignore URL parsing errors
			}

			// Network Info (not provided by Logtail)
			try {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				if ((navigator as any).connection) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					context.connectionType = (navigator as any).connection.effectiveType;
				}
			} catch (_e) {
				// Ignore connection errors
			}
		}

		return context;
	} catch (error) {
		// If anything fails, return null to not break the logging flow
		console.warn("Failed to gather log context:", error);
		return null;
	}
}

// Helper to extract GraphQL error details from ApolloError with type safety
function extractGraphQLErrorDetails(error: unknown): Record<string, unknown> | null {
	try {
		if (!error || typeof error !== "object" || error === null) {
			return null;
		}

		const errorObj = error as Record<string, unknown>;

		// Check if it's an ApolloError with graphQLErrors
		if (errorObj.graphQLErrors && Array.isArray(errorObj.graphQLErrors) && errorObj.graphQLErrors.length > 0) {
			return {
				graphQLErrors: errorObj.graphQLErrors.map((gqlError: unknown) => {
					if (!gqlError || typeof gqlError !== "object") return gqlError;
					const gqlObj = gqlError as Record<string, unknown>;
					return {
						message: gqlObj.message,
						locations: gqlObj.locations,
						path: gqlObj.path,
						extensions: gqlObj.extensions,
					};
				}),
				networkError:
					errorObj.networkError && typeof errorObj.networkError === "object"
						? {
								name: (errorObj.networkError as Record<string, unknown>).name,
								message: (errorObj.networkError as Record<string, unknown>).message,
							}
						: null,
			};
		}

		// Check if it's a plain error with GraphQL-like properties
		if (errorObj.message && (errorObj.locations || errorObj.path || errorObj.extensions)) {
			return {
				graphQLErrors: [
					{
						message: errorObj.message,
						locations: errorObj.locations,
						path: errorObj.path,
						extensions: errorObj.extensions,
					},
				],
			};
		}

		return null;
	} catch (extractError) {
		// If extraction fails, don't break the logging flow
		console.warn("Failed to extract GraphQL error details:", extractError);
		return null;
	}
}

// Simple helper to format multiple arguments with GraphQL error extraction
function formatArgs(args: unknown[]): string {
	return args
		.map(arg => {
			if (typeof arg === "object" && arg !== null) {
				try {
					// Always include the full original error object
					const baseObj = { ...arg } as Record<string, unknown>;

					// Try to extract GraphQL error details and add them
					const gqlDetails = extractGraphQLErrorDetails(arg);
					if (gqlDetails) {
						baseObj.gqlErrorDetails = gqlDetails;
					}

					// Always include the full stringified error for fallback
					baseObj.fullErrorString = JSON.stringify(arg) === "{}" ? (arg as Error).toString() : JSON.stringify(arg);

					return JSON.stringify(baseObj);
				} catch (stringifyError) {
					// Fallback: if JSON.stringify fails, convert to string
					console.warn("Failed to stringify error object:", stringifyError);
					return `[Object: ${Object.prototype.toString.call(arg)}]`;
				}
			}
			return String(arg);
		})
		.join(" ");
}

// Simplified Logtail integration with context
async function logToLogtail(level: string, message: string) {
	const token = getEnv("NEXT_PUBLIC_LOGTAIL_SOURCE_TOKEN");
	if (!token) return console.log({ level, message });

	try {
		const { Logtail } = isClient
			? await import("@logtail/browser")
			: await new Function('return import("@logtail/node")')();

		const logtail = new Logtail(token);
		const method = level === "fatal" ? "error" : level;

		// Gather context safely

		let context = {};
		try {
			context = addContextToLogger() || {};
		} catch (_err) {
			// Ignore context errors
		}

		const logData = context ? { appId, ...context } : { appId };

		if (method in logtail && typeof (logtail as Record<string, unknown>)[method] === "function") {
			((logtail as Record<string, unknown>)[method] as (message: string, data: Record<string, unknown>) => void)(
				message,
				logData,
			);
		}
	} catch (error) {
		console.error("Logtail error:", error);
		console.log({ level, message });
	}
}

// Create logger based on environment
function createLogger() {
	const level = getEnv("NEXT_PUBLIC_LOG_LEVEL", isProduction ? "info" : "debug");

	if (isProduction) {
		// Production: route to Logtail
		return pino({
			name: appId,
			level,
			browser: {
				asObject: true,
				write: {
					fatal: (...args: any[]) => logToLogtail("fatal", formatArgs(args)),
					error: (...args: any[]) => logToLogtail("error", formatArgs(args)),
					warn: (...args: any[]) => logToLogtail("warn", formatArgs(args)),
					info: (...args: any[]) => logToLogtail("info", formatArgs(args)),
					debug: (...args: any[]) => logToLogtail("debug", formatArgs(args)),
					trace: (...args: any[]) => logToLogtail("trace", formatArgs(args)),
				},
			},
		});
	}

	if (isClient) {
		// Development client: route to console
		return pino({
			name: appId,
			level: getEnv("NEXT_PUBLIC_LOG_LEVEL", isProduction ? "info" : "debug"),
			browser: {
				write: {
					fatal: (...args: any[]) => console.error(...args),
					error: (...args: any[]) => console.error(...args),
					warn: (...args: any[]) => console.warn(...args),
					info: (...args: any[]) => console.info(...args),
					debug: (...args: any[]) => console.debug(...args),
					trace: (...args: any[]) => console.trace(...args),
				},
			},
		});
	}

	// Development server: use default pino
	return pino({ name: appId, level: getEnv("LOG_LEVEL", isProduction ? "info" : "debug") });
}

const baseLogger = createLogger();

// Simple wrapper that handles multiple arguments with type safety
function createLogMethod(level: keyof typeof baseLogger) {
	return (...args: unknown[]) => {
		try {
			// Route aborted signal errors to console.error to reduce noise in remote logging
			if (level === "error" && args.some(a => isAbortError(a))) {
				console.error(...args);
				return;
			}

			// Format message for pino
			const message =
				args.length === 0 ? "" : args.length === 1 && typeof args[0] === "string" ? args[0] : formatArgs(args);

			// Call pino with just the message string
			return (baseLogger[level] as (message: string) => void)(message);
		} catch (methodError) {
			// Fallback: if logging fails, at least log to console
			console.error(`Logger error for level ${String(level)}:`, methodError);
			console.log(...args);
		}
	};
}

// Enhanced logger with GraphQL error support
export const logger = {
	...baseLogger,
	fatal: createLogMethod("fatal"),
	error: createLogMethod("error"),
	warn: createLogMethod("warn"),
	info: createLogMethod("info"),
	debug: createLogMethod("debug"),
	trace: createLogMethod("trace"),
	log: createLogMethod("info"), // Convenience method

	// Specialized GraphQL error logger with type safety and enhanced variable capture
	graphqlError: (error: ApolloError, operationName?: string, variables?: unknown) => {
		try {
			const gqlDetails = extractGraphQLErrorDetails(error);

			// Enhanced variable processing
			let processedVariables: Record<string, unknown> = {};
			try {
				if (variables !== undefined && variables !== null) {
					if (typeof variables === "object") {
						processedVariables = {
							...(variables as Record<string, unknown>),
							_variablesStringified: JSON.stringify(variables),
						};
					} else {
						processedVariables = {
							_rawValue: variables,
							_type: typeof variables,
							_stringified: String(variables),
						};
					}
				}
			} catch (variableError) {
				processedVariables = {
					_error: "Failed to process variables",
					_originalType: typeof variables,
					_errorDetails: String(variableError),
				};
			}

			const logData: Record<string, unknown> = {
				error,
				operationName: operationName || "UNKNOWN_OPERATION",
				variables: processedVariables,
				timestamp: Date.now(),
				// Always include the full error string for debugging
				fullErrorString: JSON.stringify(error) === "{}" ? (error as Error).toString() : JSON.stringify(error),
				...(gqlDetails && { graphQLDetails: gqlDetails }),
			};

			// Determine log level: use "warn" for expected client errors, "error" for actual server issues
			const firstError = error?.graphQLErrors?.[0];
			const extensionCode = firstError?.extensions?.code;
			const originalError = firstError?.extensions?.originalError as Record<string, unknown> | undefined;

			// Downgrade to warn for:
			// 1. FORBIDDEN errors
			// 2. Client-safe errors (isClientSafe: true) - these are expected user-facing errors like "Incorrect otp"
			const isClientSafeError = originalError?.isClientSafe === true;
			const isForbiddenError = extensionCode === "FORBIDDEN";
			const isUnauthenticatedError = extensionCode === "UNAUTHENTICATED";

			if (isUnauthenticatedError) {
				// Avoid sending expected auth errors to Logtail
				console.warn("GraphQL Error (UNAUTHENTICATED):", logData);
				return;
			}

			const method = isForbiddenError || isClientSafeError ? "warn" : "error";

			createLogMethod(method)("GraphQL Error:", logData);
		} catch (logError) {
			// Fallback: if everything fails, use basic error logging
			console.warn("Failed to log GraphQL error details:", logError);
			createLogMethod("error")("GraphQL Error (Fallback):", {
				error: String(error),
				operationName: operationName || "UNKNOWN_OPERATION",
				variables: String(variables),
				errorType: Object.prototype.toString.call(error),
				timestamp: Date.now(),
			});
		}
	},
};
